import type { SlideButtonRef } from '@south-street-app/molecules';

import { useCallback, useRef } from 'react';

import * as Sentry from '@sentry/react-native';
import { useMedia, YStack } from 'tamagui';
import { useShallow } from 'zustand/react/shallow';

import { en_US } from '@south-street-app/configs';
import { SlideButton } from '@south-street-app/molecules';
import {
  useGlobalBottomSheetStore,
  useMobileTradeStore,
} from '@south-street-app/stores';
import { useGetCurrentEndUser } from '@utility-nyc/react-query-sdk';

import { DisplayTradeInfo } from './DisplayTradeInfo';

const SENTRY_TAG_TYPE = 'screen';
const SENTRY_TAG_CONTEXT = 'SinglePreviewTradeOrder';

const SinglePreviewTradeOrder = () => {
  const { data } = useGetCurrentEndUser();
  const { desktop } = useMedia();
  const {
    createTradeRequest,
    conversation,
    setReceivedQuotedPrice,
    proposal,
    tradeType,
    isTwilioConnected,
  } = useMobileTradeStore(
    useShallow((state) => ({
      conversation: state.conversation,
      proposal: state.proposal,
      isTwilioConnected: state.isTwilioConnected,
      createTradeRequest: state.createTradeRequest,
      setReceivedQuotedPrice: state.setReceivedQuotedPrice,
      tradeType: state.tradeType,
    })),
  );

  const slideButtonRef = useRef<SlideButtonRef>(null);

  const openBottomSheet = useGlobalBottomSheetStore(
    (globalState) => globalState.openBottomSheet,
  );

  const firstComponent = proposal.components[0];
  const canSubmitTradeRequest = isTwilioConnected && !!conversation;

  const handleOnCompleted = useCallback(async () => {
    try {
      if (data?.data.id) {
        setReceivedQuotedPrice(false);

        await createTradeRequest({
          components: [
            {
              productId: firstComponent.product.id,
              amount: firstComponent.amount,
              type: firstComponent.type,
            },
          ],
          shares: [
            {
              amount: firstComponent.amount,
              type: firstComponent.type,
              associatedId: data.data.id,
            },
          ],
          endUserId: data.data.id,
          type: tradeType,
        });

      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          type: SENTRY_TAG_TYPE,
          context: SENTRY_TAG_CONTEXT,
          method: 'handleOnCompleted',
        },
      });

      openBottomSheet({
        type: 'backendError',
      });

      slideButtonRef.current?.reset();
    }
  }, [
    createTradeRequest,
    firstComponent,
    openBottomSheet,
    data,
    setReceivedQuotedPrice,
    tradeType,
  ]);

  return (
    <YStack
      flex={1}
      justifyContent={'space-between'}
      {...(desktop && {
        width: 800,
        marginHorizontal: '$auto',
        justifyContent: 'flex-start',
        gap: '$16',
      })}
    >
      <DisplayTradeInfo order={proposal.components[0]} />
      <SlideButton
        isLoading={!canSubmitTradeRequest}
        ref={slideButtonRef}
        label={
          canSubmitTradeRequest
            ? en_US.submitTradeRequest
            : en_US.connectingToTradeNetwork
        }
        onCompleted={handleOnCompleted}
        testID={"customer-trade-submit-slider"}
        thumbTestID={"customer-trade-submit-slider-thumb"}
      />
    </YStack>
  );
};

export { SinglePreviewTradeOrder };
